import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── PALETTE ──────────────────────────────────────────────────────────────────
const C = {
  bg:     '#0a0a0c',
  bg2:    '#111114',
  bg3:    '#1a1a24',
  gold:   '#c9a84c',
  goldL:  '#e8c97a',
  sage:   '#7aab8a',
  rose:   '#c47a7a',
  sky:    '#7a9ec4',
  text:   '#f0ede6',
  muted:  'rgba(240,237,230,0.5)',
  border: 'rgba(201,168,76,0.15)',
  dim:    '#4a4852',
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const serif    = { fontFamily: "'Cormorant Garamond', serif" };
const card     = { background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '20px' };
const btnGold  = { background: `linear-gradient(135deg,${C.gold},${C.goldL})`, color: '#0a0a0c', border: 'none', padding: '10px 24px', borderRadius: '6px', fontWeight: 600, fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'Jost, sans-serif' };
const btnGhost = { background: 'transparent', color: C.gold, border: `0.5px solid ${C.border}`, padding: '8px 18px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Jost, sans-serif' };
const tabBtn   = (a) => ({ padding: '9px 20px', borderRadius: '6px', border: `0.5px solid ${a ? C.gold : C.border}`, background: a ? 'rgba(201,168,76,0.1)' : 'transparent', color: a ? C.goldL : C.muted, fontSize: '13px', cursor: 'pointer', fontFamily: 'Jost, sans-serif', letterSpacing: '0.04em' });
const optStyle = (s) => {
  const base = { padding: '11px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', transition: 'all .15s', display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1.45, fontFamily: 'Jost, sans-serif' };
  if (s === 'correct') return { ...base, border: `0.5px solid rgba(122,171,138,0.5)`, background: 'rgba(122,171,138,0.12)', color: C.sage, cursor: 'default' };
  if (s === 'wrong')   return { ...base, border: `0.5px solid rgba(196,122,122,0.4)`, background: 'rgba(196,122,122,0.12)', color: C.rose, cursor: 'default' };
  if (s === 'dim')     return { ...base, border: `0.5px solid ${C.border}`, background: 'transparent', color: C.dim, cursor: 'default' };
  return { ...base, border: `0.5px solid ${C.border}`, background: C.bg2, color: C.text };
};

// ─── LYNN QUOTES ─────────────────────────────────────────────────────────────
const LYNN_QUOTES = [
  "I don't trade against the 50-day moving average. If price is below it, I wait. The MA is my bouncer — it decides who gets in.",
  "A Golden Cross doesn't mean buy blindly. It means the trend has shifted in your favor. Use it as context, not a trigger.",
  "The 20 MA tells me what the market is doing right now. The 50 MA tells me what it's been doing for months. I need both.",
  "When price dances along the 20 MA without breaking it, that's a healthy trend. The moment it breaks and stays below — I reassess everything.",
  "Most traders ignore moving averages until they miss a big move. Then they suddenly trust them completely. The truth is somewhere in the middle.",
  "I use the 200 MA like a map — it tells me which neighborhood we're in. Bull market or bear market. Everything else depends on that answer first.",
];

// ─── PRICE GENERATION ────────────────────────────────────────────────────────
function generatePriceSeries(n, start = 100, volatility = 1.8, drift = 0.05) {
  const prices = [start];
  let momentum = 0;
  for (let i = 1; i < n; i++) {
    const noise = (Math.random() - 0.5) * 2 * volatility;
    momentum = momentum * 0.85 + noise * 0.15;
    const change = drift + noise + momentum * 0.3;
    prices.push(Math.max(20, prices[i - 1] + change));
  }
  return prices;
}

function calcSMA(prices, period) {
  return prices.map((_, i) => {
    if (i < period - 1) return null;
    const slice = prices.slice(i - period + 1, i + 1);
    return slice.reduce((a, b) => a + b, 0) / period;
  });
}

// ─── CANVAS HELPERS ───────────────────────────────────────────────────────────
function priceToY(p, minP, maxP, h, pad = 28) {
  return pad + (1 - (p - minP) / (maxP - minP)) * (h - pad * 2);
}

function drawGrid(ctx, w, h, minP, maxP) {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = C.bg2;
  ctx.fillRect(0, 0, w, h);
  const steps = 5;
  ctx.strokeStyle = 'rgba(201,168,76,0.06)';
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= steps; i++) {
    const y = 28 + (i / steps) * (h - 56);
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    const price = maxP - (i / steps) * (maxP - minP);
    ctx.fillStyle = C.muted; ctx.font = '10px "DM Mono", monospace'; ctx.textAlign = 'left';
    ctx.fillText(price.toFixed(1), 4, y - 3);
  }
}

// ─── GAME 1: SPOT THE CROSSOVER ───────────────────────────────────────────────
function Game1({ onXpAward }) {
  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const stateRef   = useRef(null);

  const [phase, setPhase]   = useState('animating'); // animating | question | answered | done
  const [crossType, setCrossType] = useState(null);  // 'golden' | 'death'
  const [picked, setPicked] = useState(null);
  const [xpEarned, setXpEarned] = useState(0);
  const [quote, setQuote]   = useState('');

  const init = useCallback(() => {
    const prices = generatePriceSeries(120, 100, 1.6, 0.03);
    // inject a forced crossover around candle 60–80
    const crossAt = 60 + Math.floor(Math.random() * 20);
    const golden  = Math.random() < 0.5;
    // bias the series so we actually get a crossover
    const bias = golden ? 2.5 : -2.5;
    for (let i = crossAt; i < Math.min(crossAt + 15, 120); i++) {
      prices[i] = prices[i] + bias * ((i - crossAt + 1) / 5);
    }
    const ma20 = calcSMA(prices, 20);
    const ma50 = calcSMA(prices, 50);

    // find first real crossover after candle 49
    let crossIdx = -1;
    let detectedGolden = false;
    for (let i = 50; i < 120; i++) {
      if (ma20[i] !== null && ma50[i] !== null && ma20[i - 1] !== null && ma50[i - 1] !== null) {
        const prev = ma20[i - 1] - ma50[i - 1];
        const curr = ma20[i] - ma50[i];
        if (prev < 0 && curr >= 0) { crossIdx = i; detectedGolden = true; break; }
        if (prev > 0 && curr <= 0) { crossIdx = i; detectedGolden = false; break; }
      }
    }
    if (crossIdx === -1) crossIdx = crossAt; // fallback

    stateRef.current = { prices, ma20, ma50, crossIdx, detectedGolden, frame: 0, paused: false };
    setCrossType(null); setPicked(null); setPhase('animating');
    setQuote(LYNN_QUOTES[Math.floor(Math.random() * LYNN_QUOTES.length)]);
  }, []);

  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let stopped = false;

    const draw = () => {
      if (stopped || !stateRef.current) return;
      const s = stateRef.current;
      if (s.paused) return;

      const w = canvas.width, h = canvas.height;
      const { prices, ma20, ma50, crossIdx } = s;
      const n = Math.min(s.frame, prices.length);
      const visible = prices.slice(0, n);
      if (visible.length < 2) { s.frame += 2; rafRef.current = requestAnimationFrame(draw); return; }

      const minP = Math.min(...prices) * 0.99;
      const maxP = Math.max(...prices) * 1.01;
      const xStep = (w - 20) / (prices.length - 1);
      const px = (i) => 10 + i * xStep;
      const py = (p) => priceToY(p, minP, maxP, h);

      drawGrid(ctx, w, h, minP, maxP);

      // price line
      ctx.beginPath(); ctx.strokeStyle = C.text; ctx.lineWidth = 1.5;
      visible.forEach((p, i) => { i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)); });
      ctx.stroke();

      // 20 MA (gold)
      ctx.beginPath(); ctx.strokeStyle = C.gold; ctx.lineWidth = 1.8; ctx.setLineDash([]);
      let started = false;
      for (let i = 0; i < n; i++) {
        if (ma20[i] === null) continue;
        if (!started) { ctx.moveTo(px(i), py(ma20[i])); started = true; }
        else ctx.lineTo(px(i), py(ma20[i]));
      }
      ctx.stroke();

      // 50 MA (sky)
      ctx.beginPath(); ctx.strokeStyle = C.sky; ctx.lineWidth = 1.8;
      started = false;
      for (let i = 0; i < n; i++) {
        if (ma50[i] === null) continue;
        if (!started) { ctx.moveTo(px(i), py(ma50[i])); started = true; }
        else ctx.lineTo(px(i), py(ma50[i]));
      }
      ctx.stroke();

      // legend
      ctx.fillStyle = C.gold; ctx.font = '11px Jost, sans-serif'; ctx.textAlign = 'right';
      ctx.fillText('20 MA', w - 4, 16);
      ctx.fillStyle = C.sky; ctx.fillText('50 MA', w - 4, 30);

      // crossover flash
      if (n > crossIdx && n <= crossIdx + 8) {
        const alpha = 0.15 + 0.1 * Math.sin(Date.now() / 150);
        ctx.fillStyle = `rgba(201,168,76,${alpha})`;
        ctx.fillRect(px(crossIdx) - 12, 0, 24, h);
      }

      s.frame += 2;

      if (s.frame > crossIdx + 10 && phase === 'animating') {
        s.paused = true;
        setCrossType(s.detectedGolden ? 'golden' : 'death');
        setPhase('question');
        return;
      }

      if (s.frame < prices.length + 4) {
        rafRef.current = requestAnimationFrame(draw);
      }
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => { stopped = true; cancelAnimationFrame(rafRef.current); };
  }, [phase]);

  const handlePick = (isGolden) => {
    if (picked !== null) return;
    const correct = (isGolden === (crossType === 'golden'));
    setPicked(isGolden ? 'golden' : 'death');
    const xp = correct ? 80 : 20;
    setXpEarned(x => x + xp);
    onXpAward && onXpAward(xp);
    setPhase('answered');
  };

  const options = [
    { label: 'Golden Cross — 20 MA crossed above 50 MA (bullish)', val: 'golden' },
    { label: 'Death Cross — 20 MA crossed below 50 MA (bearish)', val: 'death' },
    { label: 'Price touching a support level', val: 'support' },
    { label: 'Consolidation / sideways pattern', val: 'consol' },
  ];

  const correctOpt = crossType === 'golden' ? 'golden' : 'death';
  const getState = (val) => {
    if (phase !== 'answered') return 'default';
    if (val === correctOpt) return 'correct';
    if (val === picked) return 'wrong';
    return 'dim';
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>
        Watch the 20 MA (gold) and 50 MA (blue) animate in real time. When they cross, identify what happened.
      </p>
      <div style={{ ...card, padding: '10px', marginBottom: '16px' }}>
        <canvas ref={canvasRef} width={820} height={320}
          style={{ width: '100%', height: '320px', display: 'block', borderRadius: '6px' }}/>
      </div>

      {phase === 'animating' && (
        <p style={{ fontSize: '12px', color: C.muted, textAlign: 'center' }}>Animating… watch the moving averages.</p>
      )}

      {(phase === 'question' || phase === 'answered') && (
        <div>
          <p style={{ fontSize: '14px', color: C.text, fontWeight: 500, marginBottom: '14px' }}>
            What just happened at the crossover point?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
            {options.map(({ label, val }) => (
              <div key={val} onClick={() => phase === 'question' && handlePick(val === 'golden')}
                style={optStyle(getState(val))}>
                {phase === 'answered' && val === correctOpt && <span style={{ color: C.sage }}>✓</span>}
                {phase === 'answered' && val === picked && val !== correctOpt && <span style={{ color: C.rose }}>✗</span>}
                {label}
              </div>
            ))}
          </div>
        </div>
      )}

      {phase === 'answered' && (
        <div style={{ ...card, borderColor: `rgba(201,168,76,0.25)`, marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', color: C.gold, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
            {crossType === 'golden' ? 'Golden Cross — Bullish Momentum Shift' : 'Death Cross — Bearish Momentum Shift'}
            <span style={{ marginLeft: '10px', color: picked === correctOpt ? C.sage : C.rose }}>
              {picked === correctOpt ? `+80 XP ✓` : '+20 XP'}
            </span>
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.78, marginBottom: '12px' }}>
            {crossType === 'golden'
              ? 'A Golden Cross occurs when the faster 20-period MA crosses above the slower 50-period MA. It signals that short-term momentum has turned bullish and is now stronger than the medium-term trend. Option sellers use this to confirm the direction before selling puts or running the Wheel.'
              : 'A Death Cross occurs when the faster 20-period MA crosses below the slower 50-period MA. It signals that short-term selling pressure is outpacing the medium-term trend — a bearish shift. Option sellers become more cautious: avoid new CSPs on a stock showing a Death Cross.'}
          </p>
          <div style={{ background: C.bg3, borderRadius: '6px', padding: '12px', borderLeft: `2px solid ${C.gold}`, marginBottom: '16px' }}>
            <span style={{ fontSize: '11px', color: C.gold }}>Lynn says: </span>
            <span style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7 }}>{quote}</span>
          </div>
          <button style={{ ...btnGold, width: '100%' }} onClick={init}>New Chart →</button>
        </div>
      )}

      <div style={{ fontSize: '11px', color: C.dim, textAlign: 'right', marginTop: '4px' }}>
        Session XP: +{xpEarned}
      </div>
    </div>
  );
}

// ─── GAME 2: RIDE THE BAND ────────────────────────────────────────────────────
function Game2({ onXpAward }) {
  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const dataRef     = useRef(null);

  const [phase, setPhase]   = useState('idle');   // idle | watching | predict | reveal | done
  const [touchIdx, setTouchIdx] = useState(null);
  const [picked, setPicked] = useState(null);
  const [outcome, setOutcome] = useState(null);   // 'bounce' | 'break'
  const [xpEarned, setXpEarned] = useState(0);
  const [score, setScore]   = useState(0);
  const [rounds, setRounds] = useState(0);
  const [quote, setQuote]   = useState('');

  const init = useCallback(() => {
    const base  = generatePriceSeries(80,  100, 1.4, 0.04);
    const extra = generatePriceSeries(30,  base[79], 1.6, 0.01);
    const prices = [...base, ...extra];
    const ma20   = calcSMA(prices, 20);

    // find touches of the 20 MA in the base series
    const touches = [];
    for (let i = 20; i < 78; i++) {
      if (ma20[i] !== null) {
        const dist = Math.abs(prices[i] - ma20[i]) / ma20[i];
        if (dist < 0.012) {
          if (touches.length === 0 || i - touches[touches.length - 1] > 8) touches.push(i);
        }
      }
    }
    const ti = touches.length > 0 ? touches[Math.floor(Math.random() * touches.length)] : 40;

    // determine actual outcome over next 5 candles
    const postAvg  = prices.slice(ti + 1, ti + 6).reduce((a, b) => a + b, 0) / 5;
    const maAtTouch = ma20[ti];
    const actualOutcome = postAvg > maAtTouch ? 'bounce' : 'break';

    dataRef.current = { prices, ma20, ti, actualOutcome, frame: 0, revealFrame: 0 };
    setTouchIdx(ti); setPicked(null); setOutcome(actualOutcome);
    setPhase('watching');
    setQuote(LYNN_QUOTES[Math.floor(Math.random() * LYNN_QUOTES.length)]);
  }, []);

  useEffect(() => { init(); }, [init]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dataRef.current) return;
    const ctx = canvas.getContext('2d');
    let stopped = false;

    const draw = () => {
      if (stopped || !dataRef.current) return;
      const d = dataRef.current;
      const { prices, ma20, ti } = d;
      const w = canvas.width, h = canvas.height;

      // how many price points to show
      let showN;
      if (phase === 'watching') {
        showN = Math.min(d.frame + 1, ti + 1);
        d.frame = Math.min(d.frame + 2, ti + 1);
      } else if (phase === 'predict') {
        showN = ti + 1;
      } else if (phase === 'reveal') {
        showN = Math.min(ti + 1 + d.revealFrame, ti + 8);
        d.revealFrame = Math.min(d.revealFrame + 1, 7);
      } else {
        showN = ti + 8;
      }

      const visible = prices.slice(0, showN);
      const minP = Math.min(...prices.slice(0, ti + 10)) * 0.985;
      const maxP = Math.max(...prices.slice(0, ti + 10)) * 1.015;
      const xStep = (w - 20) / (ti + 9);
      const px = (i) => 10 + i * xStep;
      const py = (p) => priceToY(p, minP, maxP, h);

      drawGrid(ctx, w, h, minP, maxP);

      // price line
      ctx.beginPath(); ctx.strokeStyle = C.text; ctx.lineWidth = 1.5;
      visible.forEach((p, i) => { i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)); });
      ctx.stroke();

      // 20 MA
      ctx.beginPath(); ctx.strokeStyle = C.gold; ctx.lineWidth = 2;
      let started = false;
      for (let i = 0; i < showN; i++) {
        if (ma20[i] === null) continue;
        if (!started) { ctx.moveTo(px(i), py(ma20[i])); started = true; }
        else ctx.lineTo(px(i), py(ma20[i]));
      }
      ctx.stroke();

      // touch marker
      if (showN > ti) {
        const pulse = 0.2 + 0.15 * Math.sin(Date.now() / 200);
        ctx.fillStyle = `rgba(201,168,76,${pulse})`;
        ctx.beginPath();
        ctx.arc(px(ti), py(prices[ti]), 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = C.gold;
        ctx.beginPath();
        ctx.arc(px(ti), py(prices[ti]), 4, 0, Math.PI * 2);
        ctx.fill();
      }

      // reveal post candles
      if (phase === 'reveal' || phase === 'done') {
        const revealColor = outcome === 'bounce' ? C.sage : C.rose;
        ctx.beginPath(); ctx.strokeStyle = revealColor; ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
        for (let i = ti; i < showN; i++) {
          i === ti ? ctx.moveTo(px(i), py(prices[i])) : ctx.lineTo(px(i), py(prices[i]));
        }
        ctx.stroke(); ctx.setLineDash([]);
      }

      ctx.fillStyle = C.gold; ctx.font = '11px Jost'; ctx.textAlign = 'right';
      ctx.fillText('20 MA', w - 4, 16);

      if (phase === 'watching' && showN < ti + 1) {
        rafRef.current = requestAnimationFrame(draw);
      } else if (phase === 'watching' && showN >= ti + 1) {
        setPhase('predict');
      } else if (phase === 'reveal') {
        rafRef.current = requestAnimationFrame(draw);
        if (d.revealFrame >= 6) setPhase('done');
      }
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => { stopped = true; cancelAnimationFrame(rafRef.current); };
  }, [phase, outcome]);

  const handlePick = (choice) => {
    if (picked !== null) return;
    setPicked(choice);
    const correct = choice === outcome;
    const xp = correct ? 60 : 20;
    setXpEarned(x => x + xp);
    setScore(s => s + (correct ? 1 : 0));
    setRounds(r => r + 1);
    onXpAward && onXpAward(xp);
    dataRef.current.revealFrame = 0;
    setPhase('reveal');
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>
        Watch price animate toward the 20 MA (gold). When it touches — predict what happens next.
      </p>
      <div style={{ ...card, padding: '10px', marginBottom: '16px' }}>
        <canvas ref={canvasRef} width={820} height={320}
          style={{ width: '100%', height: '320px', display: 'block', borderRadius: '6px' }}/>
      </div>

      {phase === 'watching' && (
        <p style={{ fontSize: '12px', color: C.muted, textAlign: 'center' }}>Watch the price approach the 20 MA…</p>
      )}

      {phase === 'predict' && (
        <div>
          <p style={{ fontSize: '14px', fontWeight: 500, color: C.text, marginBottom: '14px' }}>
            Price is touching the 20 MA. What happens next?
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={() => handlePick('bounce')}
              style={{ flex: 1, minWidth: '200px', ...btnGold, padding: '14px', fontSize: '13px', textTransform: 'none', letterSpacing: 0 }}>
              Bounce — holds the MA (trend continues)
            </button>
            <button onClick={() => handlePick('break')}
              style={{ flex: 1, minWidth: '200px', ...btnGhost, padding: '14px', fontSize: '13px', color: C.rose, borderColor: 'rgba(196,122,122,0.35)' }}>
              Break — price crosses through the MA
            </button>
          </div>
        </div>
      )}

      {(phase === 'reveal' || phase === 'done') && (
        <div>
          <div style={{ fontSize: '12px', color: C.muted, marginBottom: '8px' }}>
            Revealing next 5 candles…
          </div>
          {phase === 'done' && (
            <div style={{ ...card, borderColor: picked === outcome ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.25)', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', color: picked === outcome ? C.sage : C.rose, marginBottom: '8px', fontWeight: 500 }}>
                {picked === outcome ? `✓ Correct — ${outcome === 'bounce' ? 'Price bounced off the MA' : 'Price broke through the MA'} · +60 XP` : `✗ Wrong — ${outcome === 'bounce' ? 'Price bounced off the MA' : 'Price broke through the MA'} · +20 XP`}
              </div>
              <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.75, marginBottom: '12px' }}>
                {outcome === 'bounce'
                  ? 'The 20 MA acted as dynamic support. Price touched it and buyers stepped in — the trend remains intact. This is the classic "riding the band" setup that Lynn watches for when selling options with the trend.'
                  : 'Price broke through the 20 MA — a warning that the trend is weakening. When this happens, wait for the next candle to confirm direction before making any new trades. A close back above the MA would be a reversal signal.'}
              </p>
              <div style={{ background: C.bg3, borderRadius: '6px', padding: '12px', borderLeft: `2px solid ${C.gold}`, marginBottom: '16px' }}>
                <span style={{ fontSize: '11px', color: C.gold }}>Lynn says: </span>
                <span style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7 }}>{quote}</span>
              </div>
              <div style={{ fontSize: '11px', color: C.muted, marginBottom: '14px' }}>Round {rounds}: {score}/{rounds} correct</div>
              <button style={{ ...btnGold, width: '100%' }} onClick={init}>Next Round →</button>
            </div>
          )}
        </div>
      )}

      <div style={{ fontSize: '11px', color: C.dim, textAlign: 'right', marginTop: '4px' }}>
        Session XP: +{xpEarned} · Score: {score}/{rounds}
      </div>
    </div>
  );
}

// ─── GAME 3: PREDICT THE MA ───────────────────────────────────────────────────
function Game3({ onXpAward }) {
  const canvasRef   = useRef(null);
  const rafRef      = useRef(null);
  const pricesRef   = useRef(null);
  const ma20Ref     = useRef(null);

  const [lineY, setLineY]   = useState(160);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase]   = useState('estimate'); // estimate | revealing | done
  const [xpEarned, setXpEarned] = useState(0);
  const [result, setResult] = useState(null);
  const [quote, setQuote]   = useState('');

  const init = useCallback(() => {
    const prices = generatePriceSeries(60, 100, 1.5, 0.03);
    const ma20   = calcSMA(prices, 20);
    pricesRef.current = prices;
    ma20Ref.current   = ma20;
    setPhase('estimate');
    setResult(null);
    setLineY(160);
    setQuote(LYNN_QUOTES[Math.floor(Math.random() * LYNN_QUOTES.length)]);
  }, []);

  useEffect(() => { init(); }, [init]);

  // Draw the base chart + draggable line
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pricesRef.current) return;
    const ctx = canvas.getContext('2d');
    const prices = pricesRef.current;
    const ma20   = ma20Ref.current;
    const w = canvas.width, h = canvas.height;
    const minP = Math.min(...prices) * 0.985;
    const maxP = Math.max(...prices) * 1.015;
    const xStep = (w - 20) / (prices.length - 1);
    const px = (i) => 10 + i * xStep;
    const py = (p) => priceToY(p, minP, maxP, h);

    let rafId;
    let revealFrame = 0;
    let stopped = false;

    const render = () => {
      if (stopped) return;
      drawGrid(ctx, w, h, minP, maxP);

      // price line
      ctx.beginPath(); ctx.strokeStyle = C.text; ctx.lineWidth = 1.5;
      prices.forEach((p, i) => { i === 0 ? ctx.moveTo(px(i), py(p)) : ctx.lineTo(px(i), py(p)); });
      ctx.stroke();

      // student's line
      if (phase === 'estimate') {
        ctx.strokeStyle = C.gold; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
        ctx.beginPath(); ctx.moveTo(10, lineY); ctx.lineTo(w - 10, lineY); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = C.gold; ctx.font = 'bold 11px Jost'; ctx.textAlign = 'left';
        ctx.fillText('Your 20 MA estimate ↕ drag', 14, lineY - 6);
      }

      // reveal real MA
      if (phase === 'revealing' || phase === 'done') {
        const nShow = phase === 'revealing' ? Math.min(revealFrame * 3, prices.length) : prices.length;
        ctx.beginPath(); ctx.strokeStyle = C.gold; ctx.lineWidth = 2.5; ctx.setLineDash([]);
        let started = false;
        for (let i = 0; i < nShow; i++) {
          if (ma20[i] === null) continue;
          if (!started) { ctx.moveTo(px(i), py(ma20[i])); started = true; }
          else ctx.lineTo(px(i), py(ma20[i]));
        }
        ctx.stroke();

        // student line (faded)
        ctx.strokeStyle = 'rgba(201,168,76,0.3)'; ctx.lineWidth = 1.5; ctx.setLineDash([6, 3]);
        ctx.beginPath(); ctx.moveTo(10, lineY); ctx.lineTo(w - 10, lineY); ctx.stroke();
        ctx.setLineDash([]);

        // difference at midpoint
        if (phase === 'done') {
          const mid = 30;
          if (ma20[mid] !== null) {
            const realY = py(ma20[mid]);
            ctx.strokeStyle = C.rose; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
            ctx.beginPath(); ctx.moveTo(px(mid), lineY); ctx.lineTo(px(mid), realY); ctx.stroke();
            ctx.setLineDash([]);
            ctx.fillStyle = C.rose; ctx.font = '11px Jost'; ctx.textAlign = 'center';
            const diff = Math.abs(lineY - realY).toFixed(0);
            ctx.fillText(`Δ ${diff}px`, px(mid), Math.min(lineY, realY) - 4);
          }
        }

        // legend
        ctx.fillStyle = C.gold; ctx.font = '11px Jost'; ctx.textAlign = 'right'; ctx.setLineDash([]);
        ctx.fillText('Real 20 MA', w - 4, 16);

        if (phase === 'revealing') {
          revealFrame++;
          if (nShow < prices.length) rafId = requestAnimationFrame(render);
          else { setPhase('done'); }
        }
      }
    };

    rafId = requestAnimationFrame(render);
    return () => { stopped = true; cancelAnimationFrame(rafId); };
  }, [phase, lineY]);

  // Mouse / touch drag
  const handleMouseDown = (e) => { if (phase === 'estimate') setDragging(true); };
  const handleMouseMove = (e) => {
    if (!dragging || phase !== 'estimate') return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleY = canvasRef.current.height / rect.height;
    const rawY = (e.clientY - rect.top) * scaleY;
    setLineY(Math.max(28, Math.min(canvasRef.current.height - 28, rawY)));
  };
  const handleMouseUp   = () => setDragging(false);
  const handleTouchMove = (e) => {
    if (!dragging || phase !== 'estimate') return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleY = canvasRef.current.height / rect.height;
    const rawY = (e.touches[0].clientY - rect.top) * scaleY;
    setLineY(Math.max(28, Math.min(canvasRef.current.height - 28, rawY)));
  };

  const reveal = () => {
    const canvas = canvasRef.current;
    const prices = pricesRef.current;
    const ma20   = ma20Ref.current;
    if (!canvas || !prices || !ma20) return;

    const h = canvas.height;
    const minP = Math.min(...prices) * 0.985;
    const maxP = Math.max(...prices) * 1.015;
    const realMA = ma20[30];
    const realY  = priceToY(realMA, minP, maxP, h);
    const pctErr = Math.abs(lineY - realY) / (h - 56);

    let xp, grade, msg;
    if (pctErr < 0.04) {
      xp = 100; grade = 'Perfect'; msg = "Incredible — your eye for the 20 MA is sharp. That kind of intuition comes from studying price action closely.";
    } else if (pctErr < 0.10) {
      xp = 60; grade = 'Good'; msg = "Solid estimate. With practice, your intuition for where the moving average sits becomes second nature.";
    } else if (pctErr < 0.18) {
      xp = 30; grade = 'OK'; msg = "Not bad for a visual estimate. The 20 MA tends to lag price — look at where the average of recent candles would settle.";
    } else {
      xp = 10; grade = 'Keep Practicing'; msg = "Moving averages can be tricky to estimate visually. Try counting the last 20 candles and imagining their average price. Practice makes the eye faster.";
    }

    setResult({ xp, grade, msg, pctErr: (pctErr * 100).toFixed(1) });
    setXpEarned(x => x + xp);
    onXpAward && onXpAward(xp);
    setPhase('revealing');
  };

  const gradeColor = result
    ? result.grade === 'Perfect' ? C.sage
    : result.grade === 'Good'    ? C.goldL
    : result.grade === 'OK'      ? C.muted
    : C.rose
    : C.text;

  return (
    <div>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '14px' }}>
        Drag the gold line to where you think the 20-period moving average sits. Then reveal the real MA.
      </p>
      <div style={{ ...card, padding: '10px', marginBottom: '16px', cursor: dragging ? 'ns-resize' : phase === 'estimate' ? 'ns-resize' : 'default' }}
        onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
        onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp}>
        <canvas ref={canvasRef} width={820} height={320}
          style={{ width: '100%', height: '320px', display: 'block', borderRadius: '6px', touchAction: 'none' }}
          onMouseDown={handleMouseDown} onTouchStart={handleMouseDown}/>
      </div>

      {phase === 'estimate' && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button style={{ ...btnGold, padding: '12px 40px' }} onClick={reveal}>
            Reveal Real 20 MA →
          </button>
        </div>
      )}

      {phase === 'revealing' && (
        <p style={{ fontSize: '12px', color: C.muted, textAlign: 'center' }}>Revealing real 20 MA…</p>
      )}

      {phase === 'done' && result && (
        <div style={{ ...card, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
            <span style={{ ...serif, fontSize: '22px', color: gradeColor }}>{result.grade}</span>
            <span style={{ fontSize: '13px', color: gradeColor }}>+{result.xp} XP · Error: ~{result.pctErr}%</span>
          </div>
          <p style={{ fontSize: '13px', color: 'rgba(240,237,230,0.85)', lineHeight: 1.78, marginBottom: '12px' }}>{result.msg}</p>
          <div style={{ background: C.bg3, borderRadius: '6px', padding: '12px', borderLeft: `2px solid ${C.gold}`, marginBottom: '16px' }}>
            <span style={{ fontSize: '11px', color: C.gold }}>Lynn says: </span>
            <span style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7 }}>{quote}</span>
          </div>
          <button style={{ ...btnGold, width: '100%' }} onClick={init}>New Chart →</button>
        </div>
      )}

      <div style={{ fontSize: '11px', color: C.dim, textAlign: 'right', marginTop: '4px' }}>
        Session XP: +{xpEarned}
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────
export default function MovingAverageSimulator({ xpAward, onBack }) {
  const [mode, setMode] = useState('crossover');

  return (
    <div style={{ padding: '28px 24px', maxWidth: '860px', margin: '0 auto' }}>
      <button style={btnGhost} onClick={onBack}>← Dashboard</button>

      <div style={{ marginTop: '20px', marginBottom: '6px', fontSize: '10px', color: C.gold, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        Visual Trading Skills
      </div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: C.text, margin: '0 0 4px' }}>
        Moving Average Simulator
      </h2>
      <p style={{ fontSize: '13px', color: C.muted, marginBottom: '22px' }}>
        Three games to sharpen your understanding of the 20 MA and 50 MA.
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', flexWrap: 'wrap' }}>
        {[
          ['crossover', '◈ Spot the Crossover'],
          ['band',      '∿ Ride the Band'],
          ['predict',   '◧ Predict the MA'],
        ].map(([m, label]) => (
          <button key={m} onClick={() => setMode(m)} style={tabBtn(mode === m)}>{label}</button>
        ))}
      </div>

      {mode === 'crossover' && <Game1 onXpAward={xpAward} />}
      {mode === 'band'      && <Game2 onXpAward={xpAward} />}
      {mode === 'predict'   && <Game3 onXpAward={xpAward} />}
    </div>
  );
}
